/**
 * FutureReelsLoading - Sequential Domain Loading Screen
 * Shows sequential loading progress for all 8 domains after payment:
 * 1. First generates TEXT (future reels) via backend API
 * 2. Then generates AUDIO (TTS) for each domain
 * Each domain shows 0% → 100% progress, one after another
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Loader2, Play, ArrowRight } from 'lucide-react';
import { generatePremiumContent } from '../services/iapService';
import {
  startSequentialGeneration,
  getGenerationUserId,
  DOMAIN_ORDER,
  DOMAIN_CONFIG
} from '../services/domainTtsService';
import { rlog } from '../utils/railwayLogger';

const PHASES = { TEXT: 'text', AUDIO: 'audio', COMPLETE: 'complete' };

const FutureReelsLoading = () => {
  const navigate = useNavigate();
  const generationStartedRef = useRef(false);
  
  const [phase, setPhase] = useState(PHASES.TEXT);
  const [domainStatus, setDomainStatus] = useState(() => {
    const initial = {};
    DOMAIN_ORDER.forEach(d => { initial[d] = { progress: 0, done: false }; });
    return initial;
  });
  const [currentDomain, setCurrentDomain] = useState(null);
  const [overallProgress, setOverallProgress] = useState(0);
  const [error, setError] = useState(null);
  const [allComplete, setAllComplete] = useState(false);

  // Check if text content already exists
  const hasExistingReels = useCallback(() => {
    try {
      const ud = JSON.parse(localStorage.getItem('unified_story_data') || '{}');
      const reels = ud?.future_story?.domain_reels || {};
      const domains = Object.values(reels);
      return domains.some(lr =>
        lr && typeof lr === 'object' &&
        Object.values(lr).some(v => typeof v === 'string' && v.length > 50)
      );
    } catch { return false; }
  }, []);

  // Phase 1: Generate TEXT (future reels via backend API)
  const generateText = useCallback(async () => {
    if (hasExistingReels()) {
      console.log('Text content already exists, skipping to audio phase');
      rlog('future_reels_cached_hit', { phase: 'text' });
      // Mark all text domains as done
      setDomainStatus(prev => {
        const updated = { ...prev };
        DOMAIN_ORDER.forEach(d => { updated[d] = { progress: 100, done: true }; });
        return updated;
      });
      setOverallProgress(50);
      return true;
    }

    console.log('Starting text generation for all 8 domains...');
    rlog('future_reels_text_start', {
      domain_count: DOMAIN_ORDER.length,
      content_version: (() => { try { return JSON.parse(localStorage.getItem('content_versions') || '{}').future_reels; } catch { return null; } })(),
    });
    const textStart = Date.now();
    
    // Simulate sequential domain text loading for UX
    const domainCount = DOMAIN_ORDER.length;
    let domainsComplete = 0;

    // Start actual backend generation
    const textPromise = generatePremiumContent(['future_reels']);

    // Animate progress while waiting for backend
    const progressInterval = setInterval(() => {
      domainsComplete++;
      if (domainsComplete <= domainCount) {
        const domainKey = DOMAIN_ORDER[domainsComplete - 1];
        setCurrentDomain(domainKey);
        
        // Animate this domain from 0 → 100
        setDomainStatus(prev => ({
          ...prev,
          [domainKey]: { progress: 100, done: true }
        }));
        setOverallProgress(Math.round((domainsComplete / domainCount) * 50));
      }
      if (domainsComplete >= domainCount) {
        clearInterval(progressInterval);
      }
    }, 2500); // ~2.5s per domain visual

    try {
      const result = await textPromise;
      clearInterval(progressInterval);
      
      // Mark all domains as text-complete
      setDomainStatus(prev => {
        const updated = { ...prev };
        DOMAIN_ORDER.forEach(d => { updated[d] = { progress: 100, done: true }; });
        return updated;
      });
      setOverallProgress(50);

      if (result?.success) {
        localStorage.setItem('future_reels_ready', 'true');
        localStorage.removeItem('future_reels_generating');
        window.dispatchEvent(new Event('future-reels-ready'));
        rlog('future_reels_text_done', { elapsed_ms: Date.now() - textStart, ok: true });
        return true;
      } else {
        // Even if timeout, generation continues in background
        if (result?.error?.includes('timed out')) {
          localStorage.setItem('future_reels_ready', 'true');
          rlog('future_reels_text_timeout', { elapsed_ms: Date.now() - textStart });
          return true;
        }
        console.warn('Text generation result:', result?.error);
        rlog('future_reels_text_failed', { elapsed_ms: Date.now() - textStart, error: result?.error });
        return true; // Continue to audio anyway
      }
    } catch (err) {
      clearInterval(progressInterval);
      console.warn('Text generation error (may still be processing):', err);
      rlog('future_reels_text_exception', { elapsed_ms: Date.now() - textStart, error: err?.message });
      return true; // Don't block audio phase
    }
  }, [hasExistingReels]);

  // Phase 2: Generate AUDIO (TTS) — NON-BLOCKING with timeout
  // Text is ready, audio generates in background. Reels player handles on-demand TTS for missing chunks.
  const generateAudio = useCallback(async () => {
    setPhase(PHASES.AUDIO);
    
    // Reset domain status for audio phase
    setDomainStatus(prev => {
      const updated = { ...prev };
      DOMAIN_ORDER.forEach(d => { updated[d] = { progress: 0, done: false }; });
      return updated;
    });
    setCurrentDomain(null);

    // Check if user has paid for Future Reels (₹599+ plan)
    const unlockedFeatures = JSON.parse(localStorage.getItem('plan_unlocks') || '[]');
    if (!unlockedFeatures.includes('future_reels')) {
      console.log('Future Reels not unlocked — skipping audio generation');
      setAllComplete(true);
      setPhase(PHASES.COMPLETE);
      return;
    }

    const userId = getGenerationUserId();
    const storyDataStr = localStorage.getItem('unified_story_data');
    
    if (!storyDataStr || !userId) {
      console.log('No story data or userId for audio, completing...');
      setAllComplete(true);
      setPhase(PHASES.COMPLETE);
      return;
    }

    try {
      const storyData = JSON.parse(storyDataStr);
      const domainReels = storyData.future_story?.domain_reels ||
                          storyData.story_data?.future_story?.domain_reels;
      
      if (!domainReels) {
        setAllComplete(true);
        setPhase(PHASES.COMPLETE);
        return;
      }

      // Clear old TTS flags
      localStorage.removeItem('tts_generation_triggered');
      localStorage.removeItem('tts_generation_started');

      // Start audio generation with a MAX 45s timeout
      // Reels player will generate audio on-demand for any missing chunks
      const AUDIO_TIMEOUT_MS = 45000;
      let audioTimedOut = false;
      
      const audioPromise = startSequentialGeneration(
        userId,
        domainReels,
        // onDomainProgress
        (domainKey, progress) => {
          if (audioTimedOut) return;
          setCurrentDomain(domainKey);
          setDomainStatus(prev => ({
            ...prev,
            [domainKey]: { ...prev[domainKey], progress }
          }));
          const domainIdx = DOMAIN_ORDER.indexOf(domainKey);
          const base = 50 + (domainIdx / DOMAIN_ORDER.length) * 50;
          setOverallProgress(Math.round(base + (progress / 100) * (50 / DOMAIN_ORDER.length)));
        },
        // onDomainComplete
        (domainKey) => {
          if (audioTimedOut) return;
          setDomainStatus(prev => ({
            ...prev,
            [domainKey]: { progress: 100, done: true }
          }));
        }
      );
      
      const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => {
          audioTimedOut = true;
          console.log(`⏱️ Audio generation timed out after ${AUDIO_TIMEOUT_MS/1000}s — proceeding to reels (audio will load on-demand)`);
          resolve('timeout');
        }, AUDIO_TIMEOUT_MS);
      });
      
      await Promise.race([audioPromise, timeoutPromise]);

      setOverallProgress(100);
      setAllComplete(true);
      setPhase(PHASES.COMPLETE);
    } catch (err) {
      console.warn('Audio generation error:', err);
      setAllComplete(true);
      setPhase(PHASES.COMPLETE);
    }
  }, []);

  // Main flow: Text → Audio → Complete
  useEffect(() => {
    if (generationStartedRef.current) return;
    generationStartedRef.current = true;

    const runSequence = async () => {
      try {
        // Phase 1: Text
        await generateText();
        // Phase 2: Audio
        await generateAudio();
      } catch (err) {
        console.error('Generation sequence error:', err);
        setError(err.message);
      }
    };

    runSequence();
  }, [generateText, generateAudio]);

  const handleDomainClick = (domain) => {
    if (domainStatus[domain]?.done) {
      navigate(`/domain-story/${domain}`);
    }
  };

  const handleContinue = () => {
    navigate('/hex-story');
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex flex-col items-center justify-center p-4">
        <div className="text-red-400 text-center mb-4">{error}</div>
        <button onClick={() => navigate('/dashboard')} className="px-6 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg">
          Go to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div data-testid="future-reels-loading" className="min-h-screen bg-[#0a1628] p-4 overflow-auto">
      {/* Stars Background */}
      <div className="fixed inset-0 pointer-events-none" style={{
        backgroundImage: `radial-gradient(1px 1px at 20px 30px, rgba(255,255,255,0.5), transparent),
                         radial-gradient(1.5px 1.5px at 50px 160px, rgba(255,255,255,0.6), transparent),
                         radial-gradient(1px 1px at 80px 80px, rgba(255,255,255,0.4), transparent)`,
        backgroundSize: '100vw 100vh'
      }} />

      {/* Header */}
      <div className="relative z-10 text-center pt-6 pb-4">
        <h1 data-testid="loading-title" className="text-2xl font-bold text-white mb-2">
          {allComplete ? 'Your Future Reels are Ready!' : 'Preparing Your Future Reels'}
        </h1>
        <p className="text-white/60 text-sm">
          {phase === PHASES.TEXT && 'Generating predictions for all 8 life domains...'}
          {phase === PHASES.AUDIO && 'Creating audio narration...'}
          {phase === PHASES.COMPLETE && 'Tap any domain to explore your future'}
        </p>

        {/* Overall Progress */}
        <div className="mt-4 max-w-md mx-auto">
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              data-testid="overall-progress-bar"
              className="h-full bg-gradient-to-r from-cyan-500 to-purple-500"
              initial={{ width: 0 }}
              animate={{ width: `${overallProgress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
          <p className="text-white/50 text-xs mt-1">{overallProgress}% complete</p>
        </div>
      </div>

      {/* Domain Grid */}
      <div className="relative z-10 max-w-2xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 mt-4">
        {DOMAIN_ORDER.map((domain, idx) => {
          const config = DOMAIN_CONFIG[domain];
          const status = domainStatus[domain] || { progress: 0, done: false };
          const isCurrent = currentDomain === domain;
          const canPlay = status.done && (phase === PHASES.COMPLETE || phase === PHASES.AUDIO);

          return (
            <motion.div
              key={domain}
              data-testid={`domain-card-${domain}`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: idx * 0.08 }}
              onClick={() => canPlay && handleDomainClick(domain)}
              className={`
                relative p-4 rounded-xl border transition-all cursor-pointer
                ${status.done
                  ? 'bg-white/10 border-green-500/50'
                  : isCurrent
                    ? 'bg-white/5 border-cyan-500/50'
                    : 'bg-white/5 border-white/10'
                }
              `}
            >
              {/* Status Badge */}
              <div className="absolute top-2 right-2">
                {status.done ? (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center"
                  >
                    <Check className="w-3 h-3 text-white" />
                  </motion.div>
                ) : isCurrent ? (
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
                    <Loader2 className="w-5 h-5 text-cyan-400" />
                  </motion.div>
                ) : (
                  <div className="w-5 h-5 bg-white/10 rounded-full" />
                )}
              </div>

              {/* Domain Info */}
              <div className="text-center">
                <div className="text-2xl mb-1">{config?.icon || '✨'}</div>
                <div className="text-xs text-white/80 font-medium truncate">
                  {config?.name?.split(' ')[0] || domain}
                </div>

                {/* Progress Bar per domain */}
                <div className="mt-2 h-1 bg-white/10 rounded-full overflow-hidden">
                  <motion.div
                    className={`h-full ${status.done ? 'bg-green-400' : 'bg-cyan-400'}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${status.progress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
                <div className="text-[10px] text-white/40 mt-1">
                  {status.done ? '100%' : status.progress > 0 ? `${status.progress}%` : 'Queued'}
                </div>
              </div>

              {/* Play overlay */}
              {canPlay && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-xl opacity-0 hover:opacity-100 transition-opacity"
                >
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Play className="w-5 h-5 text-white" fill="white" />
                  </div>
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Current Status */}
      {currentDomain && !allComplete && (
        <div className="relative z-10 text-center text-white/60 text-sm mb-4">
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />
            <span>
              {phase === PHASES.TEXT ? 'Generating' : 'Creating audio for'}{' '}
              {DOMAIN_CONFIG[currentDomain]?.name || currentDomain}...
            </span>
          </div>
        </div>
      )}

      {/* All Complete */}
      <AnimatePresence>
        {allComplete && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative z-10 text-center mt-4"
          >
            <div className="text-green-400 font-semibold mb-3">
              All 8 Domain Reels Ready!
            </div>
            <motion.button
              data-testid="explore-reels-btn"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleContinue}
              className="px-8 py-3 bg-gradient-to-r from-cyan-500 to-purple-500 text-white rounded-full font-semibold inline-flex items-center gap-2"
            >
              Explore All Reels <ArrowRight className="w-4 h-4" />
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Skip */}
      <div className="relative z-10 text-center mt-6">
        <button
          data-testid="skip-btn"
          onClick={handleContinue}
          className="text-white/40 text-sm underline hover:text-white/60"
        >
          {allComplete ? 'Go to Life Domains' : 'Skip & Continue Later'}
        </button>
      </div>
    </div>
  );
};

export default FutureReelsLoading;
